/**
 * Starred Cards Page - Shows all cards the user has starred
 */
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { IoStarOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import EmptyState from '@/components/EmptyState/EmptyState';
import Spinner from '@/components/Spinner/Spinner';
import useCardIndex from '@/hooks/useCardIndex';
import { useOwnedPrintingsByName } from '@/hooks/useCollectionAvailability';
import { getDisplayImageUris } from '@/lib/cardUtils';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { ScryfallCard } from '@/types/card';
import type { StarredCard } from '@/types/lists';

import StarredCardDetailsPanel from './components/StarredCardDetailsPanel';
import { useStarredData, useToggleStar } from './hooks/api';

// Image URIs can be either full ScryfallCard image_uris or minimal card image_uris
type CardImageUris = ScryfallCard['image_uris'] | { small: string; normal: string };

interface StarredCardDisplay {
  oracle_id: string;
  name: string;
  starred_at: { seconds: number };
  imageUris: CardImageUris;
  cardName: string;
}

export default function StarredPage(): ReactElement {
  const starredData = useStarredData();
  const ownedPrintingsByName = useOwnedPrintingsByName();
  const { loading: dbLoading, getCardByOracleId, getCardById } = useCardIndex();
  const fullCardCache = useAtomValue(fullCardCacheAtom);
  const toggleStar = useToggleStar();

  // State for details panel
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  // Derive selected card from starredData
  const selectedCard: StarredCard | null = useMemo(() => {
    if (!selectedCardId) return null;
    return starredData.get(selectedCardId) ?? null;
  }, [selectedCardId, starredData]);

  // Convert starred data to displayable cards
  // Priority for image: 1. scryfall_id from fullCardCache, 2. scryfall_id minimal index, 3. inventory by name, 4. oracle_id
  const starredCards = useMemo((): StarredCardDisplay[] => {
    const cards: StarredCardDisplay[] = [];

    for (const [oracleId, starred] of starredData) {
      let imageUris: CardImageUris | undefined;
      let cardName = starred.name;

      // Priority 1: Use scryfall_id if stored, check fullCardCache first
      if (starred.scryfall_id) {
        const cachedCard = fullCardCache.get(starred.scryfall_id);
        if (cachedCard) {
          imageUris = getDisplayImageUris(cachedCard) ?? undefined;
          cardName = cachedCard.name;
        } else {
          // Try minimal card index
          const cardById = getCardById(starred.scryfall_id);
          if (cardById?.image_uris) {
            imageUris = cardById.image_uris;
            cardName = cardById.name;
          }
        }
      }

      // Priority 2: Try to match from inventory by name
      if (!imageUris && starred.name) {
        const ownedScryfallId = ownedPrintingsByName.get(starred.name.toLowerCase());
        if (ownedScryfallId) {
          const ownedCard = getCardById(ownedScryfallId);
          if (ownedCard?.image_uris) {
            imageUris = ownedCard.image_uris;
            cardName = ownedCard.name;
          }
        }
      }

      // Priority 3: Fall back to oracle_id lookup
      if (!imageUris) {
        const minimalCard = getCardByOracleId(oracleId);
        if (minimalCard?.image_uris) {
          imageUris = minimalCard.image_uris;
          cardName = minimalCard.name;
        }
      }

      if (imageUris) {
        cards.push({
          oracle_id: oracleId,
          name: starred.name,
          starred_at: starred.starred_at as { seconds: number },
          imageUris,
          cardName,
        });
      }
    }

    // Sort by starred_at descending (most recent first)
    return cards.sort((a, b) => (b.starred_at?.seconds ?? 0) - (a.starred_at?.seconds ?? 0));
  }, [starredData, ownedPrintingsByName, getCardById, getCardByOracleId, fullCardCache]);

  // Click handler for card
  const handleCardClick = useCallback((oracleId: string) => {
    setSelectedCardId(oracleId);
  }, []);

  // Close panel handler
  const handleClosePanel = useCallback(() => {
    setSelectedCardId(null);
  }, []);

  if (dbLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Starred Cards</h1>
          <p className="mt-0.5 text-sm text-zinc-500">
            {starredCards.length === 0
              ? 'No cards starred yet'
              : `${starredCards.length} card${starredCards.length === 1 ? '' : 's'} starred`}
          </p>
        </div>
        <Link
          to="/"
          className="text-platinum inline-flex items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm shadow-none transition duration-150 ease-out hover:-translate-y-0.5 hover:bg-white/15 hover:shadow-[0_12px_26px_rgba(255,255,255,0.12)] active:translate-y-0 active:shadow-none"
        >
          Browse Collection
        </Link>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {starredCards.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <EmptyState
              icon={<IoStarOutline className="h-12 w-12" />}
              title="No starred cards"
              description="Star cards from your collection to see them here. Click the star icon on any card to add it to your favorites."
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10">
            {starredCards.map((item) => (
              <CardHoverPreview key={item.oracle_id} imageUris={item.imageUris} name={item.cardName}>
                <div
                  className="group card-glow relative cursor-pointer rounded-lg"
                  onClick={() => handleCardClick(item.oracle_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleCardClick(item.oracle_id)}
                >
                  <CardImage
                    imageUris={item.imageUris}
                    name={item.cardName}
                    size="small"
                    sourceSize="normal"
                    className="aspect-5/7 h-auto w-full rounded-lg"
                  />
                  {/* Unstar button on hover */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStar(item.oracle_id, item.name);
                    }}
                    className="bg-void/80 text-gold hover:text-danger absolute bottom-1 left-1 z-10 rounded-full p-1 opacity-0 transition-opacity group-hover:opacity-100"
                    aria-label="Remove from starred"
                  >
                    <IoStarOutline className="h-4 w-4" />
                  </button>
                  {/* Card name overlay on hover */}
                  <div className="bg-void/60 pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="text-pearl line-clamp-2 px-2 text-center text-xs font-medium">{item.cardName}</span>
                  </div>
                </div>
              </CardHoverPreview>
            ))}
          </div>
        )}
      </div>

      {/* Details Panel */}
      <StarredCardDetailsPanel card={selectedCard} onClose={handleClosePanel} />
    </div>
  );
}
